import { queryKeys } from '@query/keys'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@app/context/AccountContextContext'
import { useSnackbar } from '@app/context/SnackbarContext'

import { BaseModalProps } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { RichIcon } from '@ui/components/RichIcon'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { dateAsUTC } from '@utils/date.utils'

import {
  ExportHubPayload,
  ExportHubReportFormat,
  ExportHubReportFormatId,
  ExportHubSearch,
  customRangeExportMode,
  visibleDataExportMode,
} from '@model/ExportHub'

import { DatePicker } from '@global/datePicker/DatePicker'

import { Box } from '@shared/components/Box'

interface Props extends BaseModalProps {
  exportData: Omit<ExportHubPayload, 'format' | 'mode'>
}

const EXPORT_OPTIONS = [
  { id: ExportHubReportFormatId.CSV, name: ExportHubReportFormat[ExportHubReportFormatId.CSV] },
  { id: ExportHubReportFormatId.PDF, name: ExportHubReportFormat[ExportHubReportFormatId.PDF] },
]

export const ExportModal: React.FC<Props> = (props) => {
  const { exportData, close } = props
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()
  const { account } = useAccountReadContext()
  const queryClient = useQueryClient()
  const introducingBrokerId = account?.clientIntroducingBroker?.id

  const [selectedExportType, setSelectedExportType] = useState<ExportHubReportFormatId>(
    ExportHubReportFormatId.CSV
  )

  const EXPORT_RANGE = {
    CUSTOM_RANGE: {
      value: 'custom',
      label: t('IB.Export.exportModes.customRange'),
    },
    VISIBLE_DATA: {
      value: 'visible',
      label: t('IB.Export.exportModes.visibleData'),
    },
  }

  const initStartDate = exportData?.filters?.dateFrom ? new Date(exportData.filters.dateFrom) : null
  const initEndDate = exportData?.filters?.dateTo ? new Date(exportData.filters.dateTo) : null
  const [startDate, setStartDate] = useState<Date | null>(initStartDate)
  const [endDate, setEndDate] = useState<Date | null>(initEndDate)
  const [dataToExport, setDataToExport] = useState<string>(
    startDate || endDate ? EXPORT_RANGE.CUSTOM_RANGE.value : ''
  )

  const today = new Date()
  const startMaxDate = endDate ? new Date(Math.min(endDate.getTime(), today.getTime())) : today
  const endMinDate = startDate ?? undefined
  const endMaxDate = today

  const { mutate: createExportFile, isPending } = useMutation({
    mutationFn: () => {
      if (!introducingBrokerId) {
        throw new Error('introducingBrokerId is missing')
      }

      const customDateFrom = startDate ? dateAsUTC(startDate).toISOString() : null
      const customDateTo = endDate ? dateAsUTC(endDate).toISOString() : null
      const normalizedSearch = mapExportHubSearch(exportData.filters.search)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { RegistrationDateFrom, RegistrationDateTo, ...otherSearchFilters } =
        normalizedSearch ?? {}

      const isCustomRange = dataToExport === EXPORT_RANGE.CUSTOM_RANGE.value

      const filters: ExportHubPayload['filters'] = isCustomRange
        ? {
            dateFrom: customDateFrom,
            dateTo: customDateTo,
            search: otherSearchFilters,
          }
        : {
            dateFrom: exportData.filters.dateFrom,
            dateTo: exportData.filters.dateTo,
            search: normalizedSearch,
          }
      const mode = isCustomRange ? customRangeExportMode : visibleDataExportMode
      return apiClient.createExportFile(introducingBrokerId, {
        ...exportData,
        filters,
        mode,
        format: selectedExportType,
      })
    },

    onSuccess: () => {
      close()
      addSnackbar.success({ message: t('IB.Export.exportSuccess') })

      queryClient.invalidateQueries({
        queryKey: queryKeys.ib.exportHubHasUnseen(introducingBrokerId),
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.ib.exportHub.all,
      })
    },

    onError: () => {
      addSnackbar.error({
        message: t('errors.Something went wrong'),
      })
    },
  })
  const handleExport = async () => {
    if (!exportData) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      return
    }

    createExportFile()
  }

  const isCustomRange = dataToExport === EXPORT_RANGE.CUSTOM_RANGE.value
  const isCustomRangeIncomplete = isCustomRange && (!startDate || !endDate)
  const isExportDisabled = isPending || !dataToExport || isCustomRangeIncomplete

  return (
    <Modal>
      <Modal.Header>{t('IB.Export.exportData')}</Modal.Header>
      <Modal.Body isLoading={isPending}>
        <Modal.Body.Input.SelectInput
          options={[EXPORT_RANGE.CUSTOM_RANGE, EXPORT_RANGE.VISIBLE_DATA]}
          fullWidth
          allowCopy={false}
          onChange={(v: ChangeEvent<HTMLSelectElement>) => setDataToExport(v.target.value)}
          value={dataToExport}
          disabled={false}
          label={t('IB.Export.dataToExport')}
        />

        {dataToExport === EXPORT_RANGE.CUSTOM_RANGE.value && (
          <Box flex align='center' justify='center' fullWidth gap='x16'>
            <DatePicker
              maxDate={startMaxDate}
              placement='top'
              label={t('Calendar.Start Date')}
              onChange={(date) => setStartDate(date)}
              value={startDate}
              disableYearMonthMode={false}
              size='xl'
              fullWidth
            />
            <RichIcon
              size='x24'
              icon={{ icon: ArrowIcon.Right, color: 'text-low-em' }}
              shape='circle'
            />
            <DatePicker
              minDate={endMinDate}
              maxDate={endMaxDate}
              placement='top'
              label={t('Calendar.End Date')}
              onChange={(date) => setEndDate(date)}
              value={endDate}
              disableYearMonthMode={false}
              size='xl'
              fullWidth
            />
          </Box>
        )}

        <Box
          mb={{ mobile: dataToExport === EXPORT_RANGE.CUSTOM_RANGE.value ? 'x18' : 'x0' }}
          fullWidth
        >
          <SegmentedControl
            tabs={EXPORT_OPTIONS.map((tab) => ({
              ...tab,
              onClick: () => setSelectedExportType(tab.id),
            }))}
            activeTab={selectedExportType}
          />
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close} disabled={isPending}>
          {t('Cancel')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Primary onClick={handleExport} disabled={isExportDisabled}>
          {t('Export')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
const mapExportHubSearch = (search?: ExportHubSearch): ExportHubSearch | undefined => {
  if (!search) return undefined

  const { DateFrom, DateTo, ...rest } = search

  return {
    ...rest,
    ...(DateFrom != null && { TradePeriodFrom: DateFrom as string }),
    ...(DateTo != null && { TradePeriodTo: DateTo as string }),
  }
}
